import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/auth";
import { Button } from "@shared-frontend/components/ui/button";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@shared-frontend/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shared-frontend/components/ui/dialog";
import { Textarea } from "@shared-frontend/components/ui/textarea";
import { Alert, AlertDescription } from "@shared-frontend/components/ui/alert";
import { Badge } from "@shared-frontend/components/ui/badge";
import { Separator } from "@shared-frontend/components/ui/separator";
import {
    AlertCircle,
    Building2,
    ChevronRight,
    Clock,
    LogOut,
    RefreshCw,
    X,
    Send,
    Loader2,
} from "lucide-react";
import { useFetchAvailableTenants } from "@/hooks/queries/fetchAvailableTenants";
import { useFetchMyAccessRequests } from "@/hooks/queries/fetchMyAccessRequests";
import { useCreateMyAccessRequestMutation } from "@/hooks/mutations/useCreateMyAccessRequestMutation";
import { useCancelMyAccessRequestMutation } from "@/hooks/mutations/useCancelMyAccessRequestMutation";
import type { AvailableTenant } from "@/hooks/queries/fetchAvailableTenants";

function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

/**
 * Access Request Lobby page for users who are authenticated but have no tenant access.
 * Shows available tenants to request access to and pending access requests.
 */
function AccessRequestLobbyPage() {
    const {
        requiresAccessRequest,
        accessRequestContext,
        isAuthenticated,
        logout,
        isLoading: authLoading,
    } = useAuth();

    const [selectedTenant, setSelectedTenant] = useState<AvailableTenant | null>(null);
    const [requestMessage, setRequestMessage] = useState("");
    const [requestToCancel, setRequestToCancel] = useState<string | null>(null);

    const temporaryToken = accessRequestContext?.temporaryToken ?? null;

    // Fetch available tenants
    const {
        data: availableTenants,
        isLoading: tenantsLoading,
        error: tenantsError,
        refetch: refetchTenants,
    } = useFetchAvailableTenants(temporaryToken);

    // Fetch user's pending access requests
    const {
        data: pendingRequests,
        isLoading: requestsLoading,
        error: requestsError,
        refetch: refetchRequests,
    } = useFetchMyAccessRequests(temporaryToken, "pending");

    // Mutations
    const createRequestMutation = useCreateMyAccessRequestMutation(temporaryToken);
    const cancelRequestMutation = useCancelMyAccessRequestMutation(temporaryToken);

    // Redirect if not in access request flow
    if (!requiresAccessRequest || !accessRequestContext) {
        if (isAuthenticated) {
            return <Navigate to="/" replace />;
        }
        if (!authLoading) {
            return <Navigate to="/login" replace />;
        }
        return null;
    }

    const isLoading = tenantsLoading || requestsLoading;
    const error = tenantsError || requestsError;

    async function handleRefresh() {
        await Promise.all([refetchTenants(), refetchRequests()]);
    }

    async function handleRequestAccess() {
        if (!selectedTenant) return;

        try {
            await createRequestMutation.mutateAsync({
                tenantId: selectedTenant.id,
                message: requestMessage.trim() || undefined,
            });
            setSelectedTenant(null);
            setRequestMessage("");
        } catch {
            // Error handled by mutation
        }
    }

    async function handleCancelRequest() {
        if (!requestToCancel) return;

        try {
            await cancelRequestMutation.mutateAsync(requestToCancel);
            setRequestToCancel(null);
        } catch {
            // Error handled by mutation
        }
    }

    const hasPendingRequests = pendingRequests && pendingRequests.length > 0;
    const hasAvailableTenants = availableTenants && availableTenants.length > 0;
    const noCustomer = !accessRequestContext.customerId;

    return (
        <div className="flex min-h-screen w-full items-center justify-center p-6 bg-muted/30">
            <Card className="w-full max-w-[600px]">
                <CardHeader className="space-y-3 text-center">
                    <CardTitle className="text-2xl font-semibold">
                        Request Workspace Access
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground">
                        Signed in as <span className="font-medium">{accessRequestContext.email}</span>
                    </CardDescription>
                    {noCustomer && (
                        <Alert variant="default" className="mt-4">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                Your account is not associated with an organization. Please contact your administrator.
                            </AlertDescription>
                        </Alert>
                    )}
                </CardHeader>

                <CardContent className="space-y-6">
                    {error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                {error instanceof Error ? error.message : "An error occurred"}
                            </AlertDescription>
                        </Alert>
                    )}

                    {createRequestMutation.error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                {createRequestMutation.error instanceof Error
                                    ? createRequestMutation.error.message
                                    : "Failed to submit request"}
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Available Tenants Section */}
                    {!noCustomer && (
                        <>
                            <div>
                                <h3 className="text-sm font-medium mb-3">Request access to a workspace:</h3>
                                {isLoading ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                    </div>
                                ) : hasAvailableTenants ? (
                                    <div className="space-y-2">
                                        {availableTenants.map((tenant) => (
                                            <Button
                                                key={tenant.id}
                                                variant="outline"
                                                className="w-full justify-between h-auto py-4 px-4"
                                                onClick={() => setSelectedTenant(tenant)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Building2 className="h-5 w-5 text-muted-foreground" />
                                                    <span className="font-medium">{tenant.name}</span>
                                                </div>
                                                <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                            </Button>
                                        ))}
                                    </div>
                                ) : hasPendingRequests ? (
                                    <p className="text-sm text-muted-foreground text-center py-4">
                                        You have pending requests for all available workspaces.
                                    </p>
                                ) : (
                                    <p className="text-sm text-muted-foreground text-center py-4">
                                        No workspaces available for your organization.
                                    </p>
                                )}
                            </div>

                            {/* Pending Requests Section */}
                            {hasPendingRequests && (
                                <>
                                    <Separator />
                                    <div>
                                        <h3 className="text-sm font-medium mb-3">Your pending requests:</h3>
                                        <div className="space-y-2">
                                            {pendingRequests.map((request) => (
                                                <div
                                                    key={request.id}
                                                    className="flex items-center justify-between p-4 border rounded-lg bg-background"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Building2 className="h-5 w-5 text-muted-foreground" />
                                                        <div>
                                                            <span className="font-medium">
                                                                {request.tenant?.name || "Unknown Workspace"}
                                                            </span>
                                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                                <Clock className="h-3 w-3" />
                                                                <span>Requested {formatDate(request.requestedAt)}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Badge variant="secondary" className="text-yellow-600 bg-yellow-50">
                                                            Pending
                                                        </Badge>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                                            onClick={() => setRequestToCancel(request.id)}
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between pt-4">
                        <Button variant="outline" onClick={() => logout()}>
                            <LogOut className="h-4 w-4 mr-2" />
                            Log Out
                        </Button>
                        <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
                            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
                            Refresh
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Request Access Dialog */}
            <Dialog open={!!selectedTenant} onOpenChange={(open) => !open && setSelectedTenant(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Request Access</DialogTitle>
                        <DialogDescription>
                            Request access to <span className="font-medium">{selectedTenant?.name}</span>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Textarea
                            placeholder="Add an optional message to your request (e.g., your role, why you need access)..."
                            value={requestMessage}
                            onChange={(e) => setRequestMessage(e.target.value)}
                            rows={3}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedTenant(null)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleRequestAccess}
                            disabled={createRequestMutation.isPending}
                        >
                            {createRequestMutation.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Send className="h-4 w-4 mr-2" />
                            )}
                            Submit Request
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancel Request Confirmation Dialog */}
            <Dialog open={!!requestToCancel} onOpenChange={(open) => !open && setRequestToCancel(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cancel Access Request</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to cancel this access request? You can submit a new request later.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRequestToCancel(null)}>
                            Keep Request
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleCancelRequest}
                            disabled={cancelRequestMutation.isPending}
                        >
                            {cancelRequestMutation.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <X className="h-4 w-4 mr-2" />
                            )}
                            Cancel Request
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default AccessRequestLobbyPage;
